"use client";

import { useTranslations } from "next-intl";

interface LoadingScreenProps {
  /** Headline text. Defaults to common.loading. */
  title?: string;
  /** Optional sub-message under the title. */
  subtitle?: string;
  /**
   * Variant — controls spacing/intensity. "full" for whole-page Suspense
   * fallbacks; "inline" for embedded loading states inside a card.
   */
  variant?: "full" | "inline";
}

/**
 * Branded loading screen — replaces the bare ``<Loader2 spinning>`` fallback
 * scattered across pages with a unified, brand-matching layout (logo, ring
 * spinner, headline, optional subtitle).
 *
 * Use this for any full-screen "we're loading" state. Inline button spinners
 * (small icons next to text) should keep using ``Loader2`` directly.
 */
export function LoadingScreen({
  title,
  subtitle,
  variant = "full",
}: LoadingScreenProps) {
  const t = useTranslations("common");
  const headline = title ?? t("loading");

  return (
    <div
      className={
        variant === "full"
          ? "flex min-h-screen flex-col items-center justify-center gap-8 bg-background px-6"
          : "flex flex-col items-center justify-center gap-6 py-16 px-6"
      }
      role="status"
      aria-live="polite"
    >
      {variant === "full" && (
        <img
          src="/ffaa-mark.png"
          alt=""
          aria-hidden="true"
          className="h-20 w-auto dark:brightness-110"
        />
      )}

      {/* Ring spinner: a static muted ring with a colored arc rotating on top. */}
      <div className="relative h-12 w-12">
        <div className="absolute inset-0 rounded-full border-4 border-primary/15" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary border-r-primary animate-spin" />
      </div>

      <div className="space-y-1.5 text-center max-w-sm">
        <p className="text-base font-semibold text-foreground">{headline}</p>
        {subtitle && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
